import { Transition } from "@headlessui/react";
import { useState, useEffect } from "react";
import axios from "axios";

import { useAlert } from "../../../../constants/AlertProvider";
import { Header } from "./Header";
import { Matched } from "./Matched";
import { Saved } from "./Saved";
import { Shortlisted } from "./Shortlisted";

export function Navigator() {
  const [activeTab, setActiveTab] = useState("matched");
  const [isOn, setIsOn] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const showAlert = useAlert();

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const [jobRes, matchRes] = await Promise.all([
        axios.get("/api/jobprofile/", { withCredentials: true }),
        axios.get("/api/match-results/?match_type=job", { withCredentials: true }),
      ]);
      const jobList = jobRes.data.results;
      const matches = matchRes.data.results || matchRes.data || [];
      const matchByJobId = new Map(matches.map((m) => [m.job, m]));

      const companyIds = [...new Set(jobList.map((job) => job.company).filter(Boolean))];
      const companyCache = {};
      await Promise.all(
        companyIds.map(async (id) => {
          try {
            const companyRes = await axios.get(`/api/company/${id}/`, { withCredentials: true });
            companyCache[id] = companyRes.data;
          } catch (error) {
            console.error("Error fetching company", id, error);
          }
        }),
      );

      setJobs(
        jobList.map((job) => {
          const match = matchByJobId.get(job.id);
          return {
            ...job,
            companyData: companyCache[job.company] || {},
            matchId: match?.id ?? null,
            isSaved: match?.is_saved ?? false,
            isGoodFit: match?.is_good_fit ?? false,
            isNotForMe: match?.is_not_for_me ?? false,
          };
        }),
      );
    } catch (error) {
      showAlert("Error fetching jobs", "error");
      console.error("error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line
  }, []);

  const handleJobUpdated = (jobId, matchPatch) => {
    setJobs((prev) => prev.map((job) => (job.id === jobId ? { ...job, ...matchPatch } : job)));
  };

  const tabs = [
    { id: "matched", label: "Matched for you!" },
    { id: "saved", label: "Saved" },
    { id: "shortlisted", label: "Shortlisted" },
  ];

  const tabContent = {
    matched: <Matched jobs={jobs} isLoading={isLoading} onJobUpdated={handleJobUpdated} />,
    saved: <Saved jobs={jobs} isLoading={isLoading} onJobUpdated={handleJobUpdated} />,
    shortlisted: <Shortlisted jobs={jobs} isLoading={isLoading} onJobUpdated={handleJobUpdated} />,
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  return (
    <div className="p-2 md:p-4">
      <Header isOn={isOn} setIsOn={setIsOn} />
      <div className="mt-12 p-2 md:p-4 flex flex-row md:flex-wrap gap-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`flex items-center gap-2 px-5 py-3 font-medium text-xs md:text-[14px] rounded-lg transition-all duration-300 cursor-pointer ${
              activeTab === tab.id
                ? "bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300"
                : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-700 dark:hover:text-blue-400"
            }`}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <Transition
        key={activeTab}
        appear
        enter="transition-all duration-500 ease-out"
        enterFrom="opacity-0 translate-y-4"
        enterTo="opacity-100 translate-y-0"
        show
      >
        <div className="mt-6">
          {isOn ? (
            tabContent[activeTab]
          ) : (
            <div className="mt-6 flex flex-col gap-y-2 items-center justify-center">
              <h1 className="text-gray-900 dark:text-gray-100 font-semibold text-xs md:text-[14px]">
                Matching paused
              </h1>
              <p className="text-gray-500 dark:text-gray-400 font-normal text-xs md:text-[14px]">
                Your matches will appear here once you are ready!
              </p>
            </div>
          )}
        </div>
      </Transition>
    </div>
  );
}
